import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Button,
    IconButton,
    Select,
    Box,
    Divider,
    List,
    ListItem,
    ListIcon,
} from '@chakra-ui/react';
import { Plus, Trash2, Play, GripVertical } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter, ManageDialogPrimaryButton } from './ManageDialogFrame';
import { useManageSettingsStore, MacroDefinition } from '../../state/useManageSettingsStore';
import { useCommandManager } from '../../state/commandStore';

function notify(message: string) {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message } }));
}

export function MacroManagerDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'macros');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const macros = useManageSettingsStore((state) => state.macros);
    const addMacro = useManageSettingsStore((state) => state.addMacro);
    const removeMacro = useManageSettingsStore((state) => state.removeMacro);
    const updateMacro = useManageSettingsStore((state) => state.updateMacro);
    const commands = useCommandManager((state) => state.commands);
    const executeCommand = useCommandManager((state) =>state.executeCommand); const registeredCommands = Object.values(commands); const [newMacroName, setNewMacroName] = useState(''); const [commandPickers, setCommandPickers] = useState<Record<string, string>>({});

    const handleCreate = () => {
        const name = newMacroName.trim();
        if (!name) return;
        const macro: MacroDefinition = {
            id: uuidv4(),
            name,
            description: '',
            commandIds: [],
        };
        addMacro(macro);
        setNewMacroName('');
    };

    const handleRun = (macro: MacroDefinition) => {
        if (macro.commandIds.length === 0) {
            notify(`Macro "${macro.name}" has no commands.`);
            return;
        }
        macro.commandIds.forEach((commandId) => {
            executeCommand(commandId);
        });
        notify(`Macro "${macro.name}" executed (${macro.commandIds.length} command(s)).`);
    };

    const addCommandToMacro = (macro: MacroDefinition) => {
        const picked = commandPickers[macro.id];
        if (!picked) return;
        updateMacro(macro.id, { commandIds: [...macro.commandIds, picked] });
        setCommandPickers((prev) => ({ ...prev, [macro.id]: '' }));
    };

    const removeCommandFromMacro = (macro: MacroDefinition, index: number) => {
        updateMacro(macro.id, {
            commandIds: macro.commandIds.filter((_, i) => i !== index),
        });
    };

    return (
        <ManageDialogFrame
            title={t('manage.macroManager')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <ManageDialogPrimaryButton
                            label={t('manage.newMacro')}
                            onClick={handleCreate}
                            isDisabled={!newMacroName.trim()}
                        />
                    }
                />
            }
        >
            <VStack spacing={4} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">{t('manage.aMacroIsARecordedSequenceOfRegisteredCommandsRunItToReplayTheComm')}</Text>

                <HStack spacing={2}>
                    <Input
                        size="sm"
                        placeholder={t('manage.macroName')}
                        value={newMacroName}
                        onChange={(e) => setNewMacroName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreate();
                        }}
                        bg="var(--color-bg-tertiary)"
                        borderColor="var(--color-border)"
                    />
                    <Button
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        variant="ghost"
                        onClick={handleCreate}
                        isDisabled={!newMacroName.trim()}
                    >{t('manage.create')}</Button>
                </HStack>

                <Divider borderColor="var(--color-border)" />

                {macros.length === 0 ? (
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('manage.noMacrosDefinedYet')}</Text>
                ) : (
                    <VStack align="stretch" spacing={3} maxH="45vh" overflowY="auto">
                        {macros.map((macro) => (
                            <Box
                                key={macro.id}
                                p={3}
                                borderRadius="var(--cad-radius-sm)"
                                borderWidth="1px"
                                borderColor="var(--color-border)"
                            >
                                <VStack align="stretch" spacing={2}>
                                    <HStack justify="space-between">
                                        <Text fontSize="sm" fontWeight="medium" isTruncated>
                                            {macro.name}
                                        </Text>
                                        <HStack spacing={1} flexShrink={0}>
                                            <IconButton
                                                aria-label={`Run ${macro.name}`}
                                                icon={<Play size={12} />}
                                                size="xs"
                                                variant="ghost"
                                                isDisabled={macro.commandIds.length === 0}
                                                onClick={() => handleRun(macro)}
                                            />
                                            <IconButton
                                                aria-label={`Delete ${macro.name}`}
                                                icon={<Trash2 size={12} />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => removeMacro(macro.id)}
                                            />
                                        </HStack>
                                    </HStack>

                                    {macro.commandIds.length > 0 ? (
                                        <List spacing={1}>
                                            {macro.commandIds.map((commandId, index) => {
                                                const command = commands[commandId];
                                                return (
                                                    <ListItem key={`${macro.id}-${index}`} display="flex" alignItems="center">
                                                        <ListIcon as={GripVertical} color="var(--color-text-secondary)" />
                                                        <Text fontSize="xs" isTruncated flex="1 1 auto">
                                                            {index + 1}. {command?.label ?? commandId}
                                                        </Text>
                                                        <IconButton
                                                            aria-label={t('manage.removeCommand')}
                                                            icon={<Trash2 size={10} />}
                                                            size="xs"
                                                            variant="ghost"
                                                            onClick={() => removeCommandFromMacro(macro, index)}
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    ) : (
                                        <Text fontSize="xs" color="var(--color-text-secondary)">{t('manage.noCommandsYet')}</Text>
                                    )}

                                    <HStack spacing={2}>
                                        <Select
                                            size="xs"
                                            flex="1 1 auto"
                                            placeholder={t('manage.addACommand')}
                                            value={commandPickers[macro.id] ?? ''}
                                            onChange={(e) =>
                                                setCommandPickers((prev) => ({ ...prev, [macro.id]: e.target.value }))
                                            }
                                            bg="var(--color-bg-tertiary)"
                                            borderColor="var(--color-border)"
                                        >
                                            {registeredCommands.map((command) => (
                                                <option key={command.id} value={command.id}>
                                                    {command.label}
                                                </option>
                                            ))}
                                        </Select>
                                        <IconButton
                                            aria-label={t('manage.addCommand')}
                                            icon={<Plus size={12} />}
                                            size="xs"
                                            variant="ghost"
                                            isDisabled={!commandPickers[macro.id]}
                                            onClick={() => addCommandToMacro(macro)}
                                        />
                                    </HStack>
                                </VStack>
                            </Box>
                        ))}
                    </VStack>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
